// src/components/employee/EmployeeDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useOnboarding } from '../../contexts/OnboardingContext';
import attendanceService from '../../services/attendance.service';
import leaveService from '../../services/leave.service';
import payrollService from '../../services/payroll.service';
import performanceService from '../../services/performance.service';
import vibeService from '../../services/vibe.service';
import {
    Clock, Calendar, DollarSign, FileText, Users, MessageSquare,
    Briefcase, Award, TrendingUp, CreditCard, Plane, FolderOpen,
    CheckSquare, BarChart3, HelpCircle, Target, CheckCircle, AlertCircle, Lock, Network,
    LogIn, LogOut, Megaphone, Activity, Home
} from 'lucide-react';
import { useLeave } from '../../contexts/LeaveContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import LeaveRequestModal from '../common/LeaveRequestModal';
import WorkFromHomeModal from '../common/WorkFromHomeModal';
import CelebrationHub from '../common/vibe/CelebrationHub';
import PersonalTasksWidget from '../common/tasks/PersonalTasksWidget';
import TeamTasksWidget from '../common/tasks/TeamTasksWidget';
import { formatTime, formatDate, getISTComponents } from '../../utils/dateHelpers';

const EmployeeDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { employees } = useEmployees();
    const { onboardingData, getCompletionPercentage } = useOnboarding();
    const { rawLeaveBalance, requestLeave, fetchLeaveData: refreshLeaveData } = useLeave();

    const currentEmployee = employees.find(emp => emp.email === user?.email);

    // Check if user is onboarded (admin approved)
    const isOnboarded = user?.id ? onboardingData[user.id]?.adminReviewed : false;
    const completionPercentage = user?.id ? getCompletionPercentage(user.id) : 0;

    // State for dashboard widgets
    const [dashboardData, setDashboardData] = useState({
        attendance: { present: 0, working: 0, total: 0 },
        leaveBalance: { cl: { availed: 0, total: 0 }, sl: { availed: 0, total: 0 }, lop: 0 },
        thisMonth: '--',
        goalsAchieved: '--',
        loading: true
    });

    // State for Quick Actions
    const [punchStatus, setPunchStatus] = useState({
        isPunchedIn: false,
        lastPunchTime: null,
        loading: false
    });

    // State for Announcements
    const [announcements, setAnnouncements] = useState([]);
    const [announcementsLoading, setAnnouncementsLoading] = useState(true);

    // State for Badge Counts
    const [badgeCounts, setBadgeCounts] = useState({
        taskbox: 0,
        vibe: 0
    });

    // State for Vibe Visit Tracking
    const [lastVibeVisit, setLastVibeVisit] = useState(() => {
        const saved = localStorage.getItem('employeeLastVibeVisit');
        return saved ? new Date(saved) : null;
    });

    // State for Active Session Timer
    const [activeTimer, setActiveTimer] = useState(0);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const timerRef = useRef(null);

    // State for current date and time
    const [currentDate, setCurrentDate] = useState(new Date());

    // State for clock format (true = 24-hour, false = 12-hour)
    const [is24HourFormat, setIs24HourFormat] = useState(true);

    // State for modals
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [showWFHModal, setShowWFHModal] = useState(false);

    // Debug log for dashboard
    useEffect(() => {
        if (user?.id) {
            // //console.log('📊 EmployeeDashboard - Onboarding Status:', {
            //     userId: user.id,
            //     userName: `${user.first_name} ${user.last_name}`,
            //     isOnboarded,
            //     completionPercentage,
            //     adminReviewed: onboardingData[user.id]?.adminReviewed,
            //     willShowBanner: !isOnboarded,
            //     timestamp: new Date().toISOString()
            // });
        }
    }, [user?.id, user?.first_name, user?.last_name, isOnboarded, completionPercentage, onboardingData]);

    // Log app load for activity tracking (silent, fire-and-forget)
    useEffect(() => {
        if (user?.id) {
            attendanceService.logActivity('app_load', {
                deviceType: 'web',
                metadata: { page: 'Dashboard' }
            }).catch(() => {});
        }
    }, [user?.id]);

    // Fetch dashboard data
    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!user?.id) return;

            try {
                setDashboardData(prev => ({ ...prev, loading: true }));

                // Fetch all data in parallel
                const [attendanceData, leaveData, payrollData, goalsData] = await Promise.allSettled([
                    attendanceService.getMonthlyAttendanceSummary(user), // Pass user profile for accurate working days calculation
                    leaveService.getMonthlyLeaveSummary(),
                    payrollService.getMyPayslips(),
                    performanceService.getEmployeeGoals(user.id)
                ]);

                // Process attendance data
                let attendanceValue = { present: 0, working: 0, total: 0 };
                if (attendanceData.status === 'fulfilled' && attendanceData.value) {
                    const summary = attendanceData.value;
                    console.log('summot',summary);
                    attendanceValue = {
                        present: summary.presentDays || 0,
                        working: summary.totalWorkingDays || 0,
                        total: summary.totalDaysInMonth || 0
                    };
                    console.log('📊 Attendance Summary:', summary);
                }

                // Process leave balance data
                let leaveValue = { cl: { availed: 0, total: 0 }, sl: { availed: 0, total: 0 }, lop: 0 };
                if (leaveData.status === 'fulfilled' && leaveData.value) {
                    const leave = leaveData.value;
                    leaveValue = {
                        cl: {
                            availed: leave.cl?.availed || 0,
                            total: leave.cl?.total || 0
                        },
                        sl: {
                            availed: leave.sl?.availed || 0,
                            total: leave.sl?.total || 0
                        },
                        lop: leave.lop || 0
                    };
                }

                // Process payroll data (This Month)
                let payrollValue = '--';
                if (payrollData.status === 'fulfilled' && payrollData.value) {
                    const payslips = Array.isArray(payrollData.value) ? payrollData.value : payrollData.value.payslips;
                    if (payslips && payslips.length > 0) {
                        // Get the latest payslip
                        const latestPayslip = payslips[0];
                        if (latestPayslip.net_pay !== undefined) {
                            payrollValue = `₹${parseFloat(latestPayslip.net_pay).toLocaleString('en-IN')}`;
                        } else if (latestPayslip.total_earnings !== undefined) {
                            payrollValue = `₹${parseFloat(latestPayslip.total_earnings).toLocaleString('en-IN')}`;
                        }
                    }
                }

                // Process goals data
                let goalsValue = '--';
                if (goalsData.status === 'fulfilled' && goalsData.value) {
                    const goals = Array.isArray(goalsData.value) ? goalsData.value : goalsData.value.goals;
                    if (goals && goals.length > 0) {
                        const completedGoals = goals.filter(g =>
                            g.status === 'completed' || g.progress === 100
                        ).length;
                        goalsValue = `${completedGoals}/${goals.length}`;
                    }
                }

                setDashboardData({
                    attendance: attendanceValue,
                    leaveBalance: leaveValue,
                    thisMonth: payrollValue,
                    goalsAchieved: goalsValue,
                    loading: false
                });
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                setDashboardData(prev => ({ ...prev, loading: false }));
            }
        };

        fetchDashboardData();
    }, [user?.id]);

    // Fetch punch status
    useEffect(() => {
        const fetchPunchStatus = async () => {
            if (!user?.id) return;

            try {
                const response = await attendanceService.getTodayPunches();

                // API returns {success, punches, lastPunchType, canCheckIn, canCheckOut}
                if (response && response.success) {
                    const isPunchedIn = response.lastPunchType === 'check_in';

                    // Find the most recent check-in for active session calculation
                    let lastCheckIn = null;
                    if (response.punches && Array.isArray(response.punches) && response.punches.length > 0) {
                        // Filter for check-ins and get the most recent one
                        const checkIns = response.punches.filter(p =>
                            p.punch_type === 'check_in' || p.type === 'check_in'
                        );
                        if (checkIns.length > 0) {
                            lastCheckIn = checkIns[checkIns.length - 1];
                        }
                    }

                    setPunchStatus({
                        isPunchedIn,
                        lastPunchTime: lastCheckIn?.punch_time || lastCheckIn?.created_at || null,
                        loading: false
                    });

                    // Start timer only if currently checked in, using the last check-in time
                    if (isPunchedIn && lastCheckIn) {
                        const checkInTime = new Date(lastCheckIn.punch_time || lastCheckIn.created_at);
                        if (!isNaN(checkInTime.getTime())) {
                            const now = new Date();
                            const elapsed = Math.max(0, Math.floor((now.getTime() - checkInTime.getTime()) / 1000));
                            setActiveTimer(elapsed);
                            setIsTimerActive(true);
                        }
                    } else {
                        setIsTimerActive(false);
                        setActiveTimer(0);
                    }
                } else {
                    setPunchStatus({ isPunchedIn: false, lastPunchTime: null, loading: false });
                    setIsTimerActive(false);
                    setActiveTimer(0);
                }
            } catch (error) {
                console.error('Error fetching punch status:', error);
                setPunchStatus({ isPunchedIn: false, lastPunchTime: null, loading: false });
            }
        };

        fetchPunchStatus();

        // Also refetch when component becomes visible (user navigates back)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchPunchStatus();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user?.id]);

    // Fetch announcements
    useEffect(() => {
        const fetchAnnouncements = async () => {
            if (!user?.id) return;

            try {
                setAnnouncementsLoading(true);
                // Fetch announcements from dedicated announcements API
                const response = await vibeService.getAnnouncements(3);

                // API returns {success, announcements}
                if (response && response.success && response.announcements) {
                    setAnnouncements(response.announcements);
                } else {
                    setAnnouncements([]);
                }
                setAnnouncementsLoading(false);
            } catch (error) {
                console.error('Error fetching announcements:', error);
                setAnnouncements([]);
                setAnnouncementsLoading(false);
            }
        };

        fetchAnnouncements();
    }, [user?.id]);

    // Fetch badge counts
    useEffect(() => {
        const fetchBadgeCounts = async () => {
            try {
                console.log('🔍 Employee Dashboard - Fetching dynamic badge counts...');

                // Fetch personal tasks count
                let personalTaskCount = 0;
                try {
                    const taskService = (await import('../../services/task.service')).default;
                    const personalTaskResponse = await taskService.getTasks({
                        category: 'personal',
                        status: 'pending'
                    });
                    personalTaskCount = personalTaskResponse.count || personalTaskResponse.tasks?.length || 0;
                    console.log('📊 Employee Personal Task count for badge:', personalTaskCount);
                } catch (taskError) {
                    console.warn('Employee Dashboard: Could not fetch personal task count:', taskError);
                }

                // Fetch team tasks count (if employee is part of a team)
                let teamTaskCount = 0;
                try {
                    const taskService = (await import('../../services/task.service')).default;
                    const teamTaskResponse = await taskService.getTasks({
                        category: 'team_task',
                        status: 'pending'
                    });
                    teamTaskCount = teamTaskResponse.count || teamTaskResponse.tasks?.length || 0;
                    console.log('👥 Employee Team Task count for badge:', teamTaskCount);
                } catch (teamTaskError) {
                    console.warn('Employee Dashboard: Could not fetch team task count:', teamTaskError);
                }

                // Calculate total task count (personal + team)
                const totalTaskCount = personalTaskCount + teamTaskCount;

                // Calculate vibe badge count (unseen posts from last 24h)
                let vibeCount = 0;
                try {
                    const vibeFeedResponse = await vibeService.getFeed({
                        filter: 'company',
                        limit: 20,
                        sortBy: 'recent'
                    });

                    // Count posts from last 24 hours that haven't been seen
                    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
                    const recentPosts = vibeFeedResponse.posts?.filter(post => {
                        const postDate = new Date(post.created_at);
                        const isRecent = postDate > oneDayAgo;
                        const isUnseen = !lastVibeVisit || postDate > lastVibeVisit;
                        return isRecent && isUnseen;
                    }) || [];

                    vibeCount = recentPosts.length;
                    console.log('🎉 Employee Vibe count for badge:', vibeCount, '(unseen recent posts from last 24h)');
                } catch (vibeError) {
                    console.warn('Employee Dashboard: Could not fetch vibe feed for badge:', vibeError);
                }

                setBadgeCounts({
                    taskbox: totalTaskCount,
                    vibe: vibeCount
                });

                console.log('✅ Employee Dashboard - Updated badge counts:', { taskbox: totalTaskCount, vibe: vibeCount });
            } catch (error) {
                console.error('Error fetching Employee dashboard badge counts:', error);
                setBadgeCounts({ taskbox: 0, vibe: 0 });
            }
        };

        // Fetch badge counts initially and when announcements change
        if (user?.id) {
            fetchBadgeCounts();
        }
    }, [user?.id, announcements, lastVibeVisit]);

    // Track Vibe visits
    const handleVibeVisit = () => {
        const now = new Date();
        setLastVibeVisit(now);
        localStorage.setItem('employeeLastVibeVisit', now.toISOString());
        console.log('📍 Employee Dashboard: Vibe visit tracked');
    };

    // Timer effect - increment every second when active
    useEffect(() => {
        if (isTimerActive) {
            timerRef.current = setInterval(() => {
                setActiveTimer(prev => prev + 1);
            }, 1000);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isTimerActive]);

    // Update current date and time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentDate(new Date());
        }, 1000); // Update every second for real-time clock

        return () => clearInterval(timer);
    }, []);

    // Format timer to HH:MM:SS
    const formatTimer = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Format current date to DD/MM/YYYY (IST - for Date objects)
    const formatCurrentDate = (date) => {
        const { date: istDateStr } = getISTComponents(date);
        const [year, month, day] = istDateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    // Get day of week (IST)
    const getDayOfWeek = (date) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const { dayOfWeek } = getISTComponents(date);
        return days[dayOfWeek];
    };

    // Format time as HH:MM:SS (24-hour IST)
    const formatTime24Hour = (date) => {
        const { hour, minute, second } = getISTComponents(date);
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
    };

    // Format time as HH:MM:SS AM/PM (12-hour IST)
    const formatTime12Hour = (date) => {
        const { hour, minute, second } = getISTComponents(date);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')} ${ampm}`;
    };

    // Get formatted time based on current format preference
    const getFormattedTime = (date) => {
        return is24HourFormat ? formatTime24Hour(date) : formatTime12Hour(date);
    };

    // Toggle clock format
    const toggleClockFormat = () => {
        setIs24HourFormat(prev => !prev);
    };

    // Handle punch in/out
    const handlePunch = async (type) => {
        if (punchStatus.loading) return;

        try {
            setPunchStatus(prev => ({ ...prev, loading: true }));
            // Include shift_id to help backend determine correct attendance_date for overnight shifts
            const shiftId = currentEmployee?.shift_id;
            const response = await attendanceService.punch(type, null, null, shiftId);

            // Update status using server-provided timestamp
            setPunchStatus({
                isPunchedIn: type === 'check_in',
                lastPunchTime: response.punch?.punch_time || new Date().toISOString(),
                loading: false
            });

            // Show verification feedback
            const vm = response.verificationMethod;
            if (type === 'check_in') {
                if (vm === 'wfh') {
                    alert('Working from home today. Punch recorded.');
                } else if (vm === 'none' && response.geofence?.officesConfigured) {
                    alert('Punch recorded but your location could not be verified. It has been flagged for admin review.');
                }
            }

            // Update timer
            if (type === 'check_in') {
                setActiveTimer(0);
                setIsTimerActive(true);
            } else {
                setIsTimerActive(false);
                setActiveTimer(0);
            }

            // Refresh dashboard data to update attendance widget
            const summary = await attendanceService.getMonthlyAttendanceSummary(user);
            console.log('🔄 Refreshed Attendance Summary after punch:', summary);
            if (summary.presentDays !== undefined) {
                setDashboardData(prev => ({
                    ...prev,
                    attendance: {
                        present: summary.presentDays || 0,
                        working: summary.totalWorkingDays || 0,
                        total: summary.totalDaysInMonth || 0
                    }
                }));
            }
        } catch (error) {
            console.error('Error punching:', error);
            const errorMsg = error.response?.data?.error || `Failed to punch ${type === 'check_in' ? 'in' : 'out'}. Please try again.`;
            alert(errorMsg);
            setPunchStatus(prev => ({ ...prev, loading: false }));
        }
    };

    // Handle leave request submission
    const handleLeaveRequest = async (formData) => {
        if (!currentEmployee?.id) return;

        try {
            console.log('🔍 Raw leave request data from modal:', formData);

            // Calculate total days
            const calculateLeaveDays = (fromDate, toDate, isHalfDay) => {
                if (!fromDate || !toDate) return 0;
                const start = new Date(fromDate);
                const end = new Date(toDate);
                if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                    return 0;
                }
                const diffTime = end.getTime() - start.getTime();
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                const total = diffDays >= 0 ? diffDays + 1 : 1;
                return isHalfDay ? 0.5 : total;
            };

            const totalDays = calculateLeaveDays(formData.fromDate, formData.toDate, formData.isHalfDay);

            // Use camelCase format as expected by LeaveContext.requestLeave
            const processedData = {
                leaveTypeId: formData.leaveTypeId,
                fromDate: formData.fromDate,
                toDate: formData.toDate,
                days: totalDays,
                reason: formData.reason,
                isHalfDay: formData.isHalfDay,
                emergencyContact: formData.emergencyContact
            };

            console.log('✅ Processed leave data before API call:', processedData);
            const response = await requestLeave(processedData);
            console.log('📋 API response:', response);

            if (response.success) {
                await refreshLeaveData(true);
                setShowLeaveModal(false);
                alert('Leave request submitted successfully!');
            } else {
                alert(response.message || 'Failed to submit leave request');
            }
        } catch (error) {
            console.error('Error submitting leave request:', error);
            alert('An error occurred while submitting leave request');
        }
    };

    // Handle WFH request submission
    const handleWFHSuccess = () => {
        setShowWFHModal(false);
        alert('Work from home request submitted successfully!');
    };

    // Top stats - now with real-time data
    const topStats = [
        {
            icon: Clock,
            label: 'My Attendance',
            value: dashboardData.attendance,
            color: 'text-green-600',
            bgColor: 'bg-green-100 dark:bg-green-900/30',
            isAttendance: true
        },
        {
            icon: FileText,
            label: 'My Requests',
            color: 'text-blue-600',
            bgColor: 'bg-blue-100 dark:bg-blue-900/30',
            isMyRequests: true
        },
        // Active Session widget - only shown when timer is active
        ...(isTimerActive ? [{
            icon: Activity,
            label: 'Active Session',
            value: formatTimer(activeTimer),
            color: 'text-purple-600',
            bgColor: 'bg-purple-100 dark:bg-purple-900/30',
            isTimer: true
        }] : []),
    ];

    const myAccessModules = [
        {
            id: 'taskbox',
            title: 'Task Box',
            icon: CheckSquare,
            bgColor: 'bg-gray-100 dark:bg-gray-700',
            iconColor: 'text-gray-700 dark:text-gray-300',
            route: '/employee/taskbox',
            badge: badgeCounts.taskbox
        },
        {
            id: 'vibe',
            title: 'Vibe',
            icon: MessageSquare,
            bgColor: 'bg-purple-100 dark:bg-purple-900/30',
            iconColor: 'text-purple-700 dark:text-purple-400',
            route: '/employee/vibe',
            badge: badgeCounts.vibe
        },
        {
            id: 'compensation',
            title: 'Compensation',
            icon: DollarSign,
            bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
            iconColor: 'text-yellow-700 dark:text-yellow-400',
            route: '/employee/payroll'
        },
        {
            id: 'attendance',
            title: 'Attendance',
            icon: Clock,
            bgColor: 'bg-red-100 dark:bg-red-900/30',
            iconColor: 'text-red-700 dark:text-red-400',
            route: '/employee/attendance'
        },
        {
            id: 'documents',
            title: 'Documents',
            icon: FileText,
            bgColor: 'bg-teal-100 dark:bg-teal-900/30',
            iconColor: 'text-teal-700 dark:text-teal-400',
            route: '/employee/documents'
        },
        {
            id: 'performance',
            title: 'Performance',
            icon: TrendingUp,
            bgColor: 'bg-purple-100 dark:bg-purple-900/30',
            iconColor: 'text-purple-700 dark:text-purple-400',
            route: '/employee/performance'
        },
        {
            id: 'orgview',
            title: 'Org View',
            icon: Network,
            bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
            iconColor: 'text-indigo-700 dark:text-indigo-400',
            route: '/employee/org-view'
        }
    ];

    const handleModuleClick = (route) => {
        // Track Vibe visits
        if (route === '/employee/vibe') {
            handleVibeVisit();
        }
        navigate(route);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div data-tour="dashboard-welcome">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    My Dashboard
                </h1>
                <p className="text-base text-gray-600 dark:text-gray-400 mt-1">
                    Welcome back, <span className="text-xl font-bold text-blue-600 dark:text-blue-400">{user?.first_name || user?.name || 'Employee'}</span>
                </p>
            </div>

            {/* Date and Day Display with Digital Clock */}
            <div data-tour="dashboard-clock" className="bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-3 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                            {formatCurrentDate(currentDate)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {getDayOfWeek(currentDate)}
                        </p>
                    </div>
                    <div className="text-right">
                        <p
                            onClick={toggleClockFormat}
                            className="text-2xl font-bold text-blue-600 dark:text-blue-400 font-mono cursor-pointer hover:text-blue-700 dark:hover:text-blue-300 transition-colors select-none"
                            title="Click to toggle 12/24 hour format"
                        >
                            {getFormattedTime(currentDate)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {is24HourFormat ? '24-hour' : '12-hour'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Onboarding Alert Banner - Disabled */}
            {false && !isOnboarded && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-200">
                                Complete Your Onboarding to Unlock All Features
                            </h3>
                            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                                {completionPercentage === 100
                                    ? 'Your onboarding is complete and pending admin approval. Once approved, you will have access to Attendance, Leaves, and Payroll features.'
                                    : `You're ${completionPercentage}% done with your onboarding. Complete the remaining tasks to unlock Attendance, Leaves, and Payroll features.`}
                            </p>
                            <button
                                onClick={() => navigate('/employee/onboarding')}
                                className="mt-3 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                                {completionPercentage === 100 ? 'View Onboarding Status' : 'Continue Onboarding'}
                            </button>
                        </div>
                        <Lock className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                    </div>
                </div>
            )}

            {/* Top Stats Grid */}
            <div className={`grid grid-cols-1 ${isTimerActive ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
                {topStats.map((stat, index) => {
                    const Icon = stat.icon;

                    // Render attendance widget
                    if (stat.isAttendance) {
                        return (
                            <div
                                key={index}
                                data-tour="dashboard-attendance-stats"
                                className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                                        <Icon className={`w-6 h-6 ${stat.color}`} />
                                    </div>
                                </div>
                                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    {stat.label}
                                </h3>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                                    {stat.value.present}/{stat.value.working}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Total days in month: {stat.value.total}
                                </p>
                            </div>
                        );
                    }

                    // Render My Requests widget
                    if (stat.isMyRequests) {
                        return (
                            <div
                                key={index}
                                data-tour="dashboard-quick-requests"
                                className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                                        <Icon className={`w-6 h-6 ${stat.color}`} />
                                    </div>
                                </div>
                                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-4">
                                    {stat.label}
                                </h3>
                                <div className="space-y-3">
                                    <button
                                        onClick={() => setShowLeaveModal(true)}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
                                    >
                                        <Calendar className="w-4 h-4" />
                                        Apply Leaves
                                    </button>
                                    <button
                                        onClick={() => setShowWFHModal(true)}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium text-sm"
                                    >
                                        <Home className="w-4 h-4" />
                                        Apply WFH Request
                                    </button>
                                </div>
                            </div>
                        );
                    }

                    // Render other widgets (like Active Session)
                    return (
                        <div
                            key={index}
                            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                                    <Icon className={`w-6 h-6 ${stat.color}`} />
                                </div>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {stat.label}
                            </h3>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                                {stat.value}
                            </p>
                        </div>
                    );
                })}
            </div>

            {/* Leave Request Modal */}
            {showLeaveModal && (
                <LeaveRequestModal
                    leaveTypes={rawLeaveBalance?.balance || rawLeaveBalance?.balances || []}
                    onClose={() => setShowLeaveModal(false)}
                    onSubmit={handleLeaveRequest}
                />
            )}

            {/* Work From Home Modal */}
            {showWFHModal && (
                <WorkFromHomeModal
                    isOpen={showWFHModal}
                    onClose={() => setShowWFHModal(false)}
                    managerId={currentEmployee?.reporting_manager_id}
                    onSubmitSuccess={handleWFHSuccess}
                />
            )}

            {/* Quick Actions Section - Always Enabled */}
            {true && (
                <div data-tour="dashboard-quick-actions" className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Quick Actions</h2>
                    <div className="flex gap-4">
                        <button
                            onClick={() => handlePunch('check_in')}
                            disabled={punchStatus.isPunchedIn || punchStatus.loading}
                            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                                punchStatus.isPunchedIn
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                    : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                        >
                            <LogIn className="w-5 h-5" />
                            {punchStatus.loading && !punchStatus.isPunchedIn ? 'Checking In...' : 'Check In'}
                        </button>
                        <button
                            onClick={() => handlePunch('check_out')}
                            disabled={!punchStatus.isPunchedIn || punchStatus.loading}
                            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                                !punchStatus.isPunchedIn
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                    : 'bg-red-500 hover:bg-red-600 text-white'
                            }`}
                        >
                            <LogOut className="w-5 h-5" />
                            {punchStatus.loading && punchStatus.isPunchedIn ? 'Checking Out...' : 'Check Out'}
                        </button>
                    </div>
                    {punchStatus.lastPunchTime && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 text-center">
                            Last check: {formatTime(punchStatus.lastPunchTime)}
                        </p>
                    )}
                </div>
            )}

            {/* Vibe Feature Section */}
            <div className="mb-6">
                {/* Vibe Header with Navigation */}
                <div
                    className="flex items-center gap-3 mb-4 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg p-3 transition-colors"
                    onClick={() => {
                        handleVibeVisit();
                        navigate('/employee/vibe');
                    }}
                >
                    <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-2">
                        <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Vibe</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Company announcements and celebrations</p>
                    </div>
                    <div className="text-purple-600 dark:text-purple-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>

                {/* Vibe Widgets Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Announcements Widget */}
                    <div data-tour="dashboard-announcements" className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow h-[400px] flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2">
                                <Megaphone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Announcements</h3>
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/employee/vibe');
                                }}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                            >
                                View All
                            </button>
                        </div>

                        <div className="flex-1 overflow-hidden">
                            {announcementsLoading ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : announcements.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                                    <Megaphone className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No announcements at the moment</p>
                                </div>
                            ) : (
                                <div className="p-4 space-y-3 overflow-y-auto h-full">
                                    {announcements.map((announcement) => (
                                        <div
                                            key={announcement.id}
                                            className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                                            onClick={() => navigate('/employee/vibe')}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
                                                    <Megaphone className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                            {announcement.title}
                                                        </h4>
                                                        {announcement.priority === 'high' && (
                                                            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded flex-shrink-0">
                                                                Important
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                                                        {announcement.message}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        {formatDate(announcement.created_at)} · {announcement.created_by_name || 'Admin'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Let's Celebrate Widget */}
                    <div data-tour="dashboard-celebrations" className="h-[400px]">
                        <CelebrationHub
                            theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                            onPostCreated={() => {
                                // Optional: Refresh or handle post creation
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* TaskBox Feature Section */}
            <div data-tour="dashboard-tasks" className="mb-6">
                {/* TaskBox Header with Navigation */}
                <div
                    className="flex items-center gap-3 mb-4 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg p-3 transition-colors"
                    onClick={() => navigate('/employee/taskbox')}
                >
                    <div className="bg-green-100 dark:bg-green-900 p-2 rounded-lg">
                        <CheckSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Task Box</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Personal and team task management</p>
                    </div>
                    <div className="text-green-600 dark:text-green-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>

                {/* TaskBox Widgets Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Personal Tasks Widget */}
                    <PersonalTasksWidget
                        userRole="employee"
                        dashboardView="personal"
                    />

                    {/* Team Tasks Widget */}
                    <TeamTasksWidget
                        userRole="employee"
                        dashboardView="team"
                    />
                </div>
            </div>

            {/* My Access Section */}
            <div data-tour="dashboard-my-access">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">My Access</h2>

                {/* Module Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {myAccessModules.map((module) => {
                        const Icon = module.icon;
                        return (
                            <div
                                key={module.id}
                                onClick={() => handleModuleClick(module.route)}
                                className="relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
                            >
                                <div className="p-6 flex flex-col items-center">
                                    <div className={`${module.bgColor} p-4 rounded-2xl mb-3 group-hover:scale-110 transition-transform`}>
                                        <Icon className={`w-10 h-10 ${module.iconColor}`} />
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                                        {module.title}
                                    </span>
                                    {(module.badge && Number(module.badge) > 0 && !isNaN(module.badge) && module.badge !== '0' && module.badge !== 0) ? (
                                        <span className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                                            {module.badge}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default EmployeeDashboard;